import { eventBus } from '@codestreet/event-bus'

import type { ApiResponseState } from './client.types'
import type { ApiErrorEmitPolicy } from './createApiClient.types'
import { generateEventBusName } from './generateEventBusName'
import { ResponseStatusCodes } from './responseStatusCodes'

const ERROR_CODES_ALWAYS_EMITTED: number[] = [
  ResponseStatusCodes.BANNED,
  ResponseStatusCodes.REMOVED,
]

/**
 * Emits API error events on the shared event bus.
 *
 * 1. A status event (payload `{ path }`) is emitted for any errored response
 *    that carries an HTTP status — regardless of policy.
 * 2. An error-code event (payload `error.message`) is emitted depending on
 *    `policy`:
 *    - `all`: for every error code.
 *    - `bannedRemovedOnly`: only for BANNED / REMOVED.
 */
export function emitApiError<R>(
  responseState: ApiResponseState<R>,
  path: string,
  policy: ApiErrorEmitPolicy = 'all',
): void {
  if (responseState.isError && responseState.responseStatus) {
    const statusEventName = generateEventBusName(String(responseState.responseStatus))
    eventBus.emit(statusEventName, { path })
  }

  if (!responseState.error) return

  if (
    policy === 'bannedRemovedOnly' &&
    !ERROR_CODES_ALWAYS_EMITTED.includes(responseState.error.code)
  ) {
    return
  }

  const errorEventName = generateEventBusName(String(responseState.error.code))
  eventBus.emit(errorEventName, responseState.error.message)
}
